// Admin v2 — Munkamenet kezelés (Figma ADM_10 + 10b): asztal- és gépmunkamenetek a sessions kollekcióban
// (a régi webes admin sémája: venueId, venueName, tableId, machineId, playerName, startTime, status, matchCount, revenue).
import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, DocumentData, getDocs, onSnapshot, serverTimestamp, updateDoc, UpdateData } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from '../icons';
import { fmtInt, toDate } from '../data';
import { Badge, Button, ConfirmModal, Empty, Field, Input, Modal, PageHeader, Select, Spinner, StatCard, Tone, useToast } from '../ui';

type Status = 'active' | 'paused' | 'completed';
interface S { id: string; venueId: string; venue: string; table: string; machine: string; player: string; start: Date | null; end: Date | null; status: Status; matches: number; revenue: number; pausedMs: number; pausedAt: Date | null }

const STATUS: Record<Status, { t: string; tone: Tone }> = { active: { t: 'Aktív', tone: 'green' }, paused: { t: 'Szüneteltetve', tone: 'gold' }, completed: { t: 'Befejezve', tone: 'gray' } };
const hm = (d: Date | null) => (d ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : '—');
const dur = (min: number) => (min >= 60 ? `${Math.floor(min / 60)} ó ${min % 60} p` : `${min} perc`);

export default function SessionsPage() {
  const toast = useToast();
  const [list, setList] = useState<S[] | null>(null);
  const [places, setPlaces] = useState<{ id: string; name: string }[]>([]);
  const [q, setQ] = useState('');
  const [venue, setVenue] = useState('all');
  const [status, setStatus] = useState('all');
  const [starting, setStarting] = useState(false);
  const [del, setDel] = useState<S | null>(null);
  const [, tick] = useState(0);

  useEffect(() => {
    const t = window.setInterval(() => tick((x) => x + 1), 60000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => onSnapshot(collection(db, 'sessions'), (s) => setList(s.docs.map((d) => {
    const v = d.data();
    const st = (['active', 'paused', 'completed'].includes(v.status) ? v.status : 'completed') as Status;
    return {
      id: d.id, venueId: String(v.venueId ?? ''), venue: String(v.venueName ?? ''), table: String(v.tableId ?? v.boardName ?? '—'),
      machine: String(v.machineId ?? '—'), player: String(v.playerName ?? ''), start: toDate(v.startTime), end: toDate(v.endTime),
      status: st, matches: Number(v.matchCount ?? 0), revenue: Number(v.revenue ?? 0), pausedMs: Number(v.pausedMs ?? 0), pausedAt: toDate(v.pausedAt),
    };
  }).sort((a, b) => (b.start?.getTime() ?? 0) - (a.start?.getTime() ?? 0))), () => setList([])), []);

  useEffect(() => {
    Promise.all([
      getDocs(collection(db, 'clubs')).then((s) => s.docs.map((d) => ({ id: d.id, name: String(d.data().name ?? 'Klub') }))).catch(() => []),
      getDocs(collection(db, 'venues')).then((s) => s.docs.map((d) => ({ id: d.id, name: String(d.data().name ?? 'Helyszín') }))).catch(() => []),
    ]).then(([a, b]) => setPlaces([...a, ...b].sort((x, y) => x.name.localeCompare(y.name, 'hu'))));
  }, []);

  const minutes = (s: S) => {
    if (!s.start) return 0;
    const endT = s.status === 'completed' ? s.end?.getTime() ?? Date.now() : s.status === 'paused' ? s.pausedAt?.getTime() ?? Date.now() : Date.now();
    return Math.max(0, Math.round((endT - s.start.getTime() - s.pausedMs) / 60000));
  };

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (list ?? []).filter((s) => (venue === 'all' || s.venueId === venue) && (status === 'all' || s.status === status)
      && (!t || [s.venue, s.table, s.machine, s.player].some((f) => f.toLowerCase().includes(t))));
  }, [list, q, venue, status]);

  if (!list) return <Spinner />;
  const today = new Date().toDateString();
  const set = async (s: S, patch: UpdateData<DocumentData>, ok: string) => {
    try { await updateDoc(doc(db, 'sessions', s.id), patch); toast(ok); } catch { toast('A művelet nem sikerült.', 'red'); }
  };

  return (
    <div>
      <PageHeader title="Munkamenet kezelés" sub="Asztal és gép munkamenetek kezelése" right={<Button icon="PlayCircle" onClick={() => setStarting(true)}>Munkamenet indítása</Button>} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon="PlayCircle" tone="green" label="Aktív munkamenetek" value={fmtInt(list.filter((s) => s.status === 'active').length)} valueColor="#2bc48a" />
        <StatCard icon="PauseCircle" tone="gold" label="Szüneteltetett" value={fmtInt(list.filter((s) => s.status === 'paused').length)} valueColor="#d9a514" />
        <StatCard icon="CheckCircle" tone="cyan" label="Ma befejezett" value={fmtInt(list.filter((s) => s.status === 'completed' && s.end?.toDateString() === today).length)} />
      </div>

      <div className="flex items-center gap-3 mt-6 flex-wrap">
        <Input icon="MagnifyingGlass" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés asztal, gép, játékos vagy helyszín alapján..." className="flex-1 min-w-[260px]" />
        <div className="w-[200px]"><Select value={venue} onChange={setVenue} options={[{ value: 'all', label: 'Összes helyszín' }, ...places.map((p) => ({ value: p.id, label: p.name }))]} /></div>
        <div className="w-[170px]"><Select value={status} onChange={setStatus} options={[{ value: 'all', label: 'Összes státusz' }, { value: 'active', label: 'Aktív' }, { value: 'paused', label: 'Szüneteltetve' }, { value: 'completed', label: 'Befejezve' }]} /></div>
      </div>

      <div className="mt-6 bg-[#15191E] border border-[#31383D] rounded-2xl overflow-hidden overflow-x-auto">
        <table className="w-full min-w-[1000px] text-left">
          <thead>
            <tr className="bg-[#1d232a] text-[12px] uppercase text-[#8a8f99]">
              {['Helyszín', 'Asztal', 'Gép', 'Játékos', 'Kezdés', 'Időtartam', 'Mérk.', 'Bevétel', 'Státusz', 'Műveletek'].map((h, i) => (
                <th key={h} className={`${i ? 'px-3' : 'pl-5 pr-3'} h-[39px] font-semibold`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 150).map((s) => (
              <tr key={s.id} className="border-t border-[#31383D] h-[56px] text-[13px]">
                <td className="pl-5 pr-3 font-semibold text-white">{s.venue || '—'}</td>
                <td className="px-3 text-[#B8B8C3]">{s.table}</td>
                <td className="px-3 text-[#B8B8C3]">{s.machine}</td>
                <td className="px-3 text-[#B8B8C3]">{s.player || '—'}</td>
                <td className="px-3 text-[#B8B8C3]">{hm(s.start)}</td>
                <td className="px-3 text-[#B8B8C3]">{dur(minutes(s))}</td>
                <td className="px-3 text-[#B8B8C3]">{s.matches}</td>
                <td className="px-3 text-[#B8B8C3]">{fmtInt(s.revenue)} Ft</td>
                <td className="px-3"><Badge tone={STATUS[s.status].tone}>{STATUS[s.status].t}</Badge></td>
                <td className="px-3">
                  <div className="flex gap-2">
                    {s.status === 'active' && <Sq icon="PauseCircle" c="#d9a514" t="Szüneteltetés" on={() => set(s, { status: 'paused', pausedAt: serverTimestamp() }, 'Munkamenet szüneteltetve.')} />}
                    {s.status === 'paused' && <Sq icon="PlayCircle" c="#2bc48a" t="Folytatás" on={() => set(s, { status: 'active', pausedAt: null, pausedMs: s.pausedMs + (s.pausedAt ? Date.now() - s.pausedAt.getTime() : 0) }, 'Munkamenet folytatva.')} />}
                    {s.status !== 'completed' && <Sq icon="Check" c="#2bc48a" t="Befejezés" on={() => set(s, { status: 'completed', endTime: new Date().toISOString() }, 'Munkamenet befejezve.')} />}
                    {s.status === 'completed' && <Sq icon="Trash" c="#ef4444" t="Törlés" on={() => setDel(s)} />}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <Empty>Nincs a szűrésnek megfelelő munkamenet.</Empty>}
      </div>

      {starting && <StartModal places={places} onClose={() => setStarting(false)} />}
      {del && (
        <ConfirmModal title="Munkamenet törlése" danger confirmLabel="Törlés" onClose={() => setDel(null)} text="A befejezett munkamenet véglegesen törlődik."
          onConfirm={async () => { try { await deleteDoc(doc(db, 'sessions', del.id)); toast('Munkamenet törölve.'); } catch { toast('A törlés nem sikerült.', 'red'); } setDel(null); }} />
      )}
    </div>
  );
}

function Sq({ icon, c, t, on }: { icon: string; c: string; t: string; on: () => void }) {
  return (
    <button type="button" title={t} aria-label={t} onClick={on}
      className="inline-flex items-center justify-center w-[32px] h-[32px] rounded-[10px] bg-[#1d232a] border border-[#31383D] hover:border-[#00AFF2]/50" style={{ color: c }}>
      <Icon name={icon} size={16} />
    </button>
  );
}

function StartModal({ places, onClose }: { places: { id: string; name: string }[]; onClose: () => void }) {
  const toast = useToast();
  const { user } = useAuth();
  const [venue, setVenue] = useState('');
  const [table, setTable] = useState('Table-01');
  const [machine, setMachine] = useState('Machine-01');
  const [player, setPlayer] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Modal title="Munkamenet indítása" onClose={onClose} width={480}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button icon="PlayCircle" disabled={busy || !venue} onClick={async () => {
        setBusy(true);
        try {
          await addDoc(collection(db, 'sessions'), {
            venueId: venue, venueName: places.find((p) => p.id === venue)?.name ?? '', tableId: table.trim(), machineId: machine.trim(),
            playerName: player.trim() || null, startTime: new Date().toISOString(), status: 'active', matchCount: 0, revenue: 0, pausedMs: 0,
            createdBy: user?.uid ?? null, createdAt: serverTimestamp(),
          });
          toast('Munkamenet elindítva.');
          onClose();
        } catch { toast('Az indítás nem sikerült.', 'red'); }
        setBusy(false);
      }}>Indítás</Button></>}>
      <div className="flex flex-col gap-4">
        <Field label="Helyszín"><Select value={venue} onChange={setVenue} options={[{ value: '', label: 'Válasszon helyszínt' }, ...places.map((p) => ({ value: p.id, label: p.name }))]} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Asztal azonosító"><Input value={table} onChange={(e) => setTable(e.target.value)} /></Field>
          <Field label="Gép azonosító"><Input value={machine} onChange={(e) => setMachine(e.target.value)} /></Field>
        </div>
        <Field label="Játékos neve"><Input icon="UserCircle" value={player} onChange={(e) => setPlayer(e.target.value)} placeholder="opcionális" /></Field>
      </div>
    </Modal>
  );
}
